/**
 * Sticky Add to Cart
 *
 * A bottom-anchored Add to Cart for the PDP. The fixed bar shows once the main product section has
 * scrolled entirely above the viewport and hides again when the shopper scrolls back into it. The
 * point is the same on every PDP and never measured from the primary button, and an
 * IntersectionObserver needs no recalculation on resize.
 *
 * Clicking submits the primary product form with the primary Add to Cart as the submitter, so the
 * protection plan selector and Impulse's AJAX add handle it exactly as they would a click on the
 * primary button. The bar mirrors that button's loading, disabled and label state rather than
 * tracking the request itself. Both handlers set `btn--loading` for the length of the request.
 */

if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.primary = this.form?.querySelector('[data-add-to-cart]');
      this.primaryLabel = this.primary?.querySelector('[data-add-to-cart-text]');
      this.section = this.form?.closest('.product-section');
      this.bar = this.querySelector('.sticky-add-to-cart__bar');
      this.button = this.querySelector('.sticky-add-to-cart__button');
      this.label = this.querySelector('.sticky-add-to-cart__label');

      if (this.primary?.type !== 'submit') return;

      if (!this.primaryLabel || !this.section || !this.bar || !this.button || !this.label) {
        console.warn('Sticky Add to Cart: the buy box or bar markup has changed; the bar is off.');
        return;
      }

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.button.addEventListener('click', () => this.addToCart(), { signal });
      this.form.addEventListener('ajaxProduct:error', () => this.revealError(), { signal });
      window.addEventListener('ajaxProduct:added', (event) => this.holdCartDrawer(event), {
        capture: true,
        signal,
      });
      this.bindVariantPrices(signal);

      this.primaryObserver = new MutationObserver(() => this.syncButton());
      this.primaryObserver.observe(this.primary, {
        attributes: true,
        attributeFilter: ['class', 'disabled'],
        characterData: true,
        childList: true,
        subtree: true,
      });
      this.syncButton();

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.pastSection = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.syncVisibility();
      });
      this.visibilityObserver.observe(this.section);

      this.barObserver = new ResizeObserver(() => {
        this.syncClearance();
        this.syncChatLauncher();
      });
      this.barObserver.observe(this.bar);
    }

    disconnectedCallback() {
      if (!this.abortController) return;

      this.abortController.abort();
      this.primaryObserver.disconnect();
      this.visibilityObserver.disconnect();
      this.barObserver.disconnect();
      this.chatLauncherObserver?.disconnect();
      clearTimeout(this.chatLauncherTimer);
      this.classList.remove('is-visible');
      this.syncChatLauncher();
      this.chatLauncherObserver = null;
      document.documentElement.style.removeProperty('--bfx-sticky-add-to-cart-height');
    }

    syncVisibility() {
      this.setVisible(Boolean(this.pastSection && this.wasEnabled));
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.bar.inert = !visible;
      this.syncChatLauncher();
    }

    syncClearance() {
      document.documentElement.style.setProperty(
        '--bfx-sticky-add-to-cart-height',
        `${this.bar.offsetHeight}px`
      );
    }

    syncChatLauncher() {
      if (!this.chatLauncherReady || !this.hasChatLauncher()) {
        this.awaitChatLauncher();
        return;
      }

      const launcher = document.getElementById('launcher');

      this.chatBaseOffset ??= this.readChatBaseOffset(launcher);
      this.chatOffset ??= this.chatBaseOffset;

      const lift = this.classList.contains('is-visible') ? this.bar.offsetHeight : 0;
      const vertical = this.chatBaseOffset + lift;
      if (vertical === this.chatOffset) return;

      this.chatOffset = vertical;
      window.zE('messenger:set', 'customization', {
        position: { offset: { web: { vertical }, mobile: { vertical } } },
      });
    }

    awaitChatLauncher() {
      if (this.chatLauncherObserver || this.abortController.signal.aborted) return;

      if (this.hasChatLauncher()) {
        this.chatLauncherReady = true;
        this.syncChatLauncher();
        return;
      }

      this.chatLauncherObserver = new MutationObserver((_mutations, observer) => {
        if (!this.hasChatLauncher()) return;
        observer.disconnect();
        this.chatLauncherTimer = setTimeout(() => {
          this.chatLauncherReady = true;
          this.syncChatLauncher();
        });
      });
      this.chatLauncherObserver.observe(document.head, { childList: true });
      this.chatLauncherObserver.observe(document.body, { childList: true, subtree: true });
    }

    hasChatLauncher() {
      return typeof window.zE === 'function' && Boolean(document.getElementById('launcher'));
    }

    readChatBaseOffset(launcher) {
      const bottom = parseFloat(getComputedStyle(launcher).bottom);
      return Number.isFinite(bottom) ? bottom : 16;
    }

    isBusy() {
      return this.primary.classList.contains('btn--loading');
    }

    addToCart() {
      if (this.isBusy() || this.primary.disabled) return;
      this.form.requestSubmit(this.primary);
    }

    revealError() {
      const errors = this.form.querySelector('.errors');
      if (!errors || !this.classList.contains('is-visible')) return;
      errors.scrollIntoView({ block: 'center' });
      this.primary.focus({ preventScroll: true });
      this.addFailed = true;
      setTimeout(() => {
        this.addFailed = false;
      });
    }

    // Impulse follows a failed add with ajaxProduct:added on document, in the same task, which
    // would open the cart drawer over the message just brought into view. The rebuild the drawer
    // would have run still happens.
    holdCartDrawer(event) {
      if (!this.addFailed) return;
      event.stopPropagation();
      window.bfxCart.requestBuild();
    }

    syncButton() {
      const busy = this.isBusy();
      this.button.classList.toggle('is-loading', busy);
      this.button.setAttribute('aria-busy', String(busy));
      this.button.disabled = this.primary.disabled;
      this.label.textContent = busy
        ? this.button.dataset.addingText
        : this.primaryLabel.textContent.trim();

      if (this.wasEnabled || this.primary.disabled) return;
      this.wasEnabled = true;
      if (this.pastSection) this.syncVisibility();
    }

    // Impulse rewrites the primary price on a variant change; this keeps the bar's in step.
    bindVariantPrices(signal) {
      const source = this.querySelector('[data-variant-prices]');
      const price =
        this.querySelector('.bfx-promo-price__regular') ??
        this.querySelector('.bfx-promo-price__current');
      if (!source || !price) return;

      let prices;
      try {
        prices = JSON.parse(source.textContent);
      } catch (error) {
        console.warn(
          'Sticky Add to Cart: unreadable variant prices; price will not follow variant changes.',
          error
        );
        return;
      }

      this.section.addEventListener(
        'variantChange',
        ({ detail }) => {
          const amount = prices[detail?.variant?.id];
          if (amount) price.textContent = amount;
        },
        { signal }
      );
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
